import { useMemo } from "react";
import { JobPhase } from "generated/graphql";
import pluralMeasurementMaterial from "constants/pluralMeasurementMaterial";
import { sentenceCase } from "change-case";
import { PlantRow, parsePlantAddress } from "./utils";
import { getWorkOrderPlantTrucks } from "../workOrderPlantTrucks";

export default function usePlantRows(jobPhase: JobPhase): PlantRow[] {
  const hasPlantProducts = jobPhase?.jobPhasePlants && jobPhase.jobPhasePlants.length > 0;
  const hasProposalPlantProduct = Boolean(jobPhase?.proposalProduct?.plantProduct);

  return useMemo(() => {
    const proposalMaterials = jobPhase?.proposalProduct?.proposalPlantProducts ?? [];

    if (!proposalMaterials.length && !hasPlantProducts && !hasProposalPlantProduct) return [];

    const sourcePlants = proposalMaterials.length
      ? proposalMaterials.map((material) => ({
          id: material.id,
          proposalPlantProductId: material.id,
          plant: material.plant,
          plantProduct: { id: material.plantProductId, type: material.type },
          name: material.plant?.name,
          type: material.type,
          materialNeeded: material.materialNeededOverride ?? material.materialNeeded,
        }))
      : hasPlantProducts
      ? jobPhase.jobPhasePlants ?? []
      : [
          {
            id: `proposal-plant-${
              jobPhase?.proposalProduct?.plantProduct?.plantProductId ?? "fallback"
            }`,
            proposalPlantProductId: jobPhase?.proposalProduct?.plantProduct?.id,
            plant: {
              ...jobPhase?.proposalProduct?.plantProduct?.plant,
              id: jobPhase?.proposalProduct?.plantProduct?.plant?.plantId,
            },
            plantProduct: {
              id: jobPhase?.proposalProduct?.plantProduct?.plantProductId,
              type: jobPhase?.proposalProduct?.plantProduct?.type,
            },
            name: jobPhase?.proposalProduct?.plantProduct?.plant?.name,
            type: jobPhase?.proposalProduct?.plantProduct?.type,
            materialNeeded:
              jobPhase?.proposalProduct?.plantProduct?.materialNeededOverride ??
              jobPhase?.proposalProduct?.plantProduct?.materialNeeded ??
              jobPhase?.proposalProduct?.material?.materialNeededOverride ??
              jobPhase?.proposalProduct?.material?.materialNeeded,
          },
        ];

    return (
      sourcePlants.flatMap((plant) => {
        const matchingTrucks = getWorkOrderPlantTrucks(
          plant,
          sourcePlants,
          jobPhase.workOrderTransferTrucks ?? []
        );

        const worksheetMaterial =
          "worksheetMaterial" in plant ? plant.worksheetMaterial : undefined;
        const materialDisplay = worksheetMaterial
          ? `${worksheetMaterial.quantity ?? "—"} ${worksheetMaterial.unit ?? ""}`.trim()
          : `${plant.materialNeeded ?? "—"} ${
              pluralMeasurementMaterial[jobPhase?.proposalProduct?.measurementMaterial] ?? ""
            }`.trim();

        if (!matchingTrucks.length) {
          return [
            {
              key: `${plant.id}-no-truck`,
              plantName: plant.plant?.name || plant.name || "Plant",
              address: parsePlantAddress(plant.plant),
              material: sentenceCase(plant.plantProduct?.type || plant.type || "Material"),
              tons: materialDisplay,
              truckName: "—",
              truckCount: "—",
            },
          ];
        }

        return matchingTrucks.map((truck) => {
          const proposalTruck = jobPhase.proposalProduct?.transferTrucks?.find(
            (proposalTransferTruck) =>
              proposalTransferTruck?.proposalPlantProductId === truck?.proposalPlantProductId &&
              (`${proposalTransferTruck?.transferTruckId}` === `${truck?.transferTruckId}` ||
                proposalTransferTruck?.name === truck?.name)
          );

          const billing = truck.worksheetBilling;
          const truckCount =
            proposalTruck?.quantity ?? proposalTruck?.trucksNeeded ?? truck?.quantity ?? "—";

          return {
            key: `${plant.id}-${truck?.id}`,
            plantName: plant.plant?.name || plant.name || "Plant",
            address: parsePlantAddress(plant.plant),
            material: sentenceCase(plant.plantProduct?.type || plant.type || "Material"),
            tons: materialDisplay,
            truckName: proposalTruck?.name || truck?.name || "—",
            truckCount: billing
              ? `${truckCount} trucks · ${billing.quantity} ${billing.unit}`
              : truckCount,
          };
        });
      }) ?? []
    );
  }, [hasPlantProducts, hasProposalPlantProduct, jobPhase]);
}
